import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { IMAGES, scrollToId, waLink } from "@/config";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "5", label: "Flight · Hotel · Package · Bus · Train" },
  { value: "₹0", label: "Train booking fee above ₹2,000" },
  { value: "1:1", label: "A real person on WhatsApp" }
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const typeY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="container-edit grid grid-cols-1 items-center gap-14 py-14 sm:py-20 lg:grid-cols-12 lg:gap-10 lg:py-24">
        <div className="lg:col-span-7">
          <motion.p
            className="eyebrow mb-8 flex items-center gap-4"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          >
            <span className="inline-block h-px w-10 bg-cyan" />
            Naditra — We get you better travel prices
          </motion.p>

          <motion.h1
            style={{ y: typeY }}
            className="font-serif text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl"
            data-testid="hero-title"
          >
            <Line delay={0.25}>Pay less on</Line>
            <Line delay={0.38}>
              <em className="font-medium italic text-ocean">every trip</em>
            </Line>
            <Line delay={0.51}>you take.</Line>
          </motion.h1>

          <motion.p
            className="mt-8 max-w-xl text-base leading-relaxed text-ink2 sm:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          >
            Send us the price you're getting for your flight, hotel, holiday package, bus or
            train ticket. Our travel expert finds you a better price before you book. And
            train tickets above ₹2,000? Zero booking fees.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          >
            <button data-testid="hero-cta-button" onClick={() => scrollToId("get-started")} className="btn-ocean">
              Get a better price
              <ArrowDownRight className="h-4 w-4" />
            </button>
            <button
              data-testid="hero-whatsapp-button"
              onClick={() => window.open(waLink("Hi! I want a better price on my trip."), "_blank")}
              className="btn-outline"
            >
              <MessageCircle className="h-4 w-4" />
              Chat on WhatsApp
            </button>
          </motion.div>

          <motion.dl
            className="mt-14 grid grid-cols-1 gap-6 border-t border-ink/15 pt-8 sm:grid-cols-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
          >
            {STATS.map((s) => (
              <div key={s.value} className="flex items-baseline gap-3 sm:block">
                <dt className="sr-only">{s.label}</dt>
                <dd className="font-serif text-2xl font-medium text-ocean sm:text-3xl">{s.value}</dd>
                <dd className="mt-1.5 text-[13px] font-semibold text-ink2">{s.label}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            className="relative"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
          >
            <div className="absolute -right-3 -top-3 h-full w-full border border-cyan" aria-hidden="true" />
            <div className="relative aspect-[3/4] overflow-hidden border border-ink/20 bg-ivory2">
              <motion.img
                style={{ y: imgY }}
                src={IMAGES.hero}
                alt="Indian palace hotel at dusk — one of the categories Naditra prices for you"
                className="h-[115%] w-full scale-105 object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/35 via-transparent to-transparent" aria-hidden="true" />
            </div>
            <div className="absolute bottom-5 left-5 flex items-center gap-2 border border-ivory/30 bg-ink/60 px-4 py-2.5 backdrop-blur-md" data-testid="hero-badge">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan" />
              </span>
              <span className="text-xs font-bold text-ivory">
                A real person will reply on WhatsApp
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
